import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, MapPin, Waves, Star, Plus, MessageCircle, Navigation, ChevronRight, ChevronDown, Filter, Camera, X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/PageShell';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import { ShakaIcon } from './spots/constants';
import SpotCard from './spots/SpotCard';
import SpotDetail from './spots/SpotDetail';
import AddSpotModal from './spots/AddSpotModal';
import { authFetch, API } from '../utils/api';


export default function Spots() {
  const navigate = useNavigate();
  const [spots, setSpots] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [starredSpots, setStarredSpots] = useState([]);
  const [reactions, setReactions] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [tab, setTab] = useState('all');
  const [nearbySpots, setNearbySpots] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);

  // Filters & pagination
  const [filters, setFilters] = useState(null); // { total, regions, has_cam_count }
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [hasCam, setHasCam] = useState(false);
  const [regionSearch, setRegionSearch] = useState('');
  const [showRegionPicker, setShowRegionPicker] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 50;

  // Load filter options
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/api/spots/filters`);
        if (res.ok) setFilters(await res.json());
      } catch {}
    })();
  }, []);

  // Build API params from current filters
  const buildParams = useCallback((pg = 1) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedRegion) params.set('region', selectedRegion);
    if (selectedSource) params.set('source', selectedSource);
    if (hasCam) params.set('has_cam', 'true');
    params.set('page', pg);
    params.set('limit', PAGE_SIZE);
    return params.toString();
  }, [query, selectedRegion, selectedSource, hasCam]);

  const loadSpots = useCallback(async (pg = 1, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await authFetch(`${API}/api/spots?${buildParams(pg)}`);
      if (res.ok) {
        const data = await res.json();
        if (append) {
          setSpots(prev => [...prev, ...(data.spots || [])]);
        } else {
          setSpots(data.spots || []);
        }
        setTotalCount(data.total || 0);
        setHasMore(data.has_more || false);
        setPage(data.page || pg);
      }
    } catch {} finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams]);

  const loadStarred = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/spots/starred`);
      if (res.ok) setStarredSpots((await res.json()).favorite_spots || []);
    } catch {}
  }, []);

  const loadReactions = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/spots/reactions`);
      if (res.ok) setReactions((await res.json()).reactions || {});
    } catch {}
  }, []);

  // Initial load
  useEffect(() => { loadStarred(); loadReactions(); loadNearby(); }, [loadStarred, loadReactions]);

  // Reload spots when filters change
  useEffect(() => {
    if (tab !== 'nearby' && tab !== 'faves' && tab !== 'liked') {
      loadSpots(1, false);
    }
  }, [query, selectedRegion, selectedSource, hasCam, tab, loadSpots]);

  const loadNearby = async () => {
    setNearbyLoading(true);
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              const res = await authFetch(`${API}/api/spots/nearby?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`);
              if (res.ok) setNearbySpots((await res.json()).spots || []);
            } catch {} finally { setNearbyLoading(false); }
          },
          () => {
            authFetch(`${API}/api/nearby-spots`)
              .then(r => r.ok ? r.json() : null)
              .then(d => { if (d?.spots) setNearbySpots(d.spots.map(s => ({ name: s, source: 'nearby' }))); })
              .catch(() => {}).finally(() => setNearbyLoading(false));
          },
          { timeout: 5000 }
        );
      } else { setNearbyLoading(false); }
    } catch { setNearbyLoading(false); }
  };

  const toggleStar = async (spotName) => {
    const isStarred = starredSpots.includes(spotName);
    const endpoint = isStarred ? '/api/spots/unstar' : '/api/spots/star';
    setStarredSpots(prev => isStarred ? prev.filter(s => s !== spotName) : [...prev, spotName]);
    if (!isStarred && !reactions[spotName.toLowerCase()]) {
      setReactions(prev => ({ ...prev, [spotName.toLowerCase()]: 'shaka' }));
    }
    try {
      const res = await fetch(`${API}${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ spot: spotName }) });
      if (res.ok) setStarredSpots((await res.json()).favorite_spots || []);
    } catch { setStarredSpots(prev => isStarred ? [...prev, spotName] : prev.filter(s => s !== spotName)); }
    loadReactions();
  };

  const handleReact = async (spotName, reaction) => {
    const prev = reactions[spotName.toLowerCase()];
    setReactions(r => {
      const next = { ...r };
      if (reaction === 'none') delete next[spotName.toLowerCase()];
      else next[spotName.toLowerCase()] = reaction;
      return next;
    });
    try {
      await authFetch(`${API}/api/spots/react`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ spot: spotName, reaction }) });
    } catch {
      setReactions(r => {
        const next = { ...r };
        if (prev) next[spotName.toLowerCase()] = prev;
        else delete next[spotName.toLowerCase()];
        return next;
      });
    }
  };

  const handleSearch = (e) => { e.preventDefault(); };
  const handleSpotAdded = () => { loadSpots(1, false); loadReactions(); };

  const clearFilters = () => {
    setSelectedRegion('');
    setSelectedSource('');
    setHasCam(false);
    setQuery('');
  };

  const activeFilterCount = [selectedRegion, selectedSource, hasCam].filter(Boolean).length;

  // Filtered regions for picker search
  const filteredRegions = useMemo(() => {
    if (!filters?.regions) return [];
    if (!regionSearch) return filters.regions;
    const s = regionSearch.toLowerCase();
    return filters.regions.filter(r => r.name.toLowerCase().includes(s));
  }, [filters, regionSearch]);

  // Display count — for faves/liked tabs use local data
  const displayCount = tab === 'faves' ? starredSpots.length
    : tab === 'liked' ? Object.values(reactions).filter(r => r === 'shaka').length
    : tab === 'nearby' ? nearbySpots.length
    : totalCount;

  return (
    <PageShell title="Spots">
      <SEO title="Surf Spots" description="Explore surf spots, check live conditions, and find your next wave with SurfSwell.ai." path="/spots" />
      <AnimatePresence mode="wait">
        {selectedSpot ? (
          <SpotDetail key="detail" name={selectedSpot} isStarred={starredSpots.includes(selectedSpot)} myReaction={reactions[selectedSpot.toLowerCase()] || null} onStar={toggleStar} onReact={handleReact} onClose={() => setSelectedSpot(null)} />
        ) : (
          <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-5" data-testid="spots-page">

            {/* Dynamic counter */}
            <div className="flex items-center justify-between mb-3" data-testid="spots-counter">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <MapPin className="w-3.5 h-3.5 text-primary" />
                </div>
                <div>
                  <p className="text-lg font-bold leading-tight tabular-nums" data-testid="spots-count">{displayCount.toLocaleString()}</p>
                  <p className="text-[9px] text-muted-foreground/50 -mt-0.5">
                    {tab === 'faves' ? 'favorite spots' : tab === 'liked' ? 'liked spots' : tab === 'nearby' ? 'spots nearby' : 'spots'}
                    {activeFilterCount > 0 && tab === 'all' && <span className="text-primary/60 ml-1">({activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''})</span>}
                  </p>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="text-[10px] text-primary/60 hover:text-primary font-medium flex items-center gap-0.5" data-testid="spots-clear-filters">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>

            {/* Search + Add */}
            <div className="flex gap-2 mb-3">
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/40" />
                  <input data-testid="spots-search-input" type="text" value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search spots, regions..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-8 pr-3 py-2.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40" />
                </div>
              </form>
              <button data-testid="add-spot-btn" onClick={() => setShowAddModal(true)}
                className="px-3 py-2.5 rounded-xl bg-primary/15 border border-primary/20 text-primary hover:bg-primary/25 transition-colors flex items-center gap-1.5 text-xs font-medium flex-shrink-0">
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>

            {/* Tab filters */}
            <div className="flex gap-1 mb-3 bg-bg-subtle/50 rounded-xl p-1" data-testid="spots-filter-tabs">
              {[
                { id: 'all', label: 'All' },
                { id: 'nearby', label: 'Nearby', icon: Navigation },
                { id: 'faves', label: 'Faves', icon: Star },
                { id: 'liked', label: 'Liked', iconCustom: true },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`spots-filter-${t.id}`}
                  className={`flex-1 flex items-center justify-center gap-1 text-[10px] py-1.5 rounded-lg font-medium transition-all ${
                    tab === t.id
                      ? t.id === 'faves' ? 'bg-yellow-400/15 text-yellow-400'
                        : t.id === 'liked' ? 'bg-primary/20 text-primary'
                        : t.id === 'nearby' ? 'bg-green-500/15 text-green-400'
                        : 'bg-white/10 text-white'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t.iconCustom ? <ShakaIcon className="w-3 h-3" /> : t.icon ? <t.icon className="w-3 h-3" /> : null}
                  {t.label}
                </button>
              ))}
            </div>

            {/* Advanced filters — only for "All" tab */}
            {tab === 'all' && (
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="spots-advanced-filters">
                {/* Region picker */}
                <div className="relative">
                  <button
                    data-testid="spots-region-filter-btn"
                    onClick={() => { setShowRegionPicker(!showRegionPicker); setRegionSearch(''); }}
                    className={`text-[10px] px-2.5 py-1.5 rounded-lg border transition-all flex items-center gap-1 ${
                      selectedRegion ? 'bg-primary/15 border-primary/30 text-primary' : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                    }`}
                  >
                    <MapPin className="w-3 h-3" />
                    {selectedRegion || 'Region'}
                    <ChevronDown className="w-2.5 h-2.5" />
                  </button>
                  <AnimatePresence>
                    {showRegionPicker && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="absolute z-30 top-full mt-1 left-0 w-56 bg-bg-paper border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                        data-testid="spots-region-picker"
                      >
                        <div className="p-2 border-b border-white/5">
                          <input
                            data-testid="spots-region-search"
                            autoFocus
                            value={regionSearch}
                            onChange={e => setRegionSearch(e.target.value)}
                            placeholder="Search regions..."
                            className="w-full bg-white/5 rounded-lg px-2.5 py-1.5 text-[11px] outline-none placeholder:text-muted-foreground/30"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <button
                            onClick={() => { setSelectedRegion(''); setShowRegionPicker(false); }}
                            className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-white/5 transition-colors flex justify-between ${!selectedRegion ? 'text-primary font-medium' : 'text-muted-foreground'}`}
                          >
                            <span>All Regions</span>
                            <span className="text-[9px] text-muted-foreground/40">{filters?.total?.toLocaleString()}</span>
                          </button>
                          {filteredRegions.map(r => (
                            <button
                              key={r.name}
                              onClick={() => { setSelectedRegion(r.name); setShowRegionPicker(false); }}
                              className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-white/5 transition-colors flex justify-between ${selectedRegion === r.name ? 'text-primary font-medium' : 'text-muted-foreground'}`}
                            >
                              <span className="truncate mr-2">{r.name}</span>
                              <span className="text-[9px] text-muted-foreground/40 flex-shrink-0">{r.count}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Source filter */}
                {[
                  { id: '', label: 'All Sources' },
                  { id: 'spitcast', label: 'Spitcast' },
                  { id: 'popular', label: 'Popular' },
                  { id: 'community', label: 'Community' },
                ].map(s => (
                  <button
                    key={s.id}
                    data-testid={`spots-source-${s.id || 'all'}`}
                    onClick={() => setSelectedSource(selectedSource === s.id ? '' : s.id)}
                    className={`text-[10px] px-2.5 py-1.5 rounded-lg border transition-all ${
                      (s.id === '' && !selectedSource) || selectedSource === s.id
                        ? 'bg-primary/15 border-primary/30 text-primary'
                        : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                    } ${s.id === '' ? 'hidden' : ''}`}
                  >
                    {s.label}
                  </button>
                ))}

                {/* Webcam filter */}
                <button
                  data-testid="spots-cam-filter"
                  onClick={() => setHasCam(!hasCam)}
                  className={`text-[10px] px-2.5 py-1.5 rounded-lg border transition-all flex items-center gap-1 ${
                    hasCam ? 'bg-primary/15 border-primary/30 text-primary' : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                  }`}
                >
                  <Camera className="w-3 h-3" />
                  Webcam
                </button>
              </div>
            )}

            {/* Close region picker on outside click */}
            {showRegionPicker && <div className="fixed inset-0 z-20" onClick={() => setShowRegionPicker(false)} />}

            {/* Starred chips */}
            {starredSpots.length > 0 && !query && tab === 'all' && !selectedRegion && !selectedSource && !hasCam && (
              <div className="mb-3">
                <p className="text-[10px] tracking-widest uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><Star className="w-3 h-3 text-yellow-400 fill-yellow-400" /> Your Spots</p>
                <div className="flex flex-wrap gap-2 pb-1">
                  {starredSpots.map(n => (
                    <button key={n} onClick={() => setSelectedSpot(n)} data-testid={`starred-chip-${n.replace(/\s+/g, '-').toLowerCase()}`}
                      className="flex items-center gap-1.5 bg-yellow-400/10 border border-yellow-400/20 rounded-xl px-3 py-1.5 text-[11px] text-yellow-300 font-medium hover:bg-yellow-400/15 transition-colors">
                      <Star className="w-3 h-3 fill-yellow-400" />{n}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            {tab === 'nearby' ? (
              nearbyLoading ? (
                <div className="flex items-center justify-center py-16"><div className="flex space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full typing-dot"></div><div className="w-2 h-2 bg-green-400 rounded-full typing-dot"></div><div className="w-2 h-2 bg-green-400 rounded-full typing-dot"></div>
                </div></div>
              ) : nearbySpots.length === 0 ? (
                <div className="text-center py-12" data-testid="no-nearby-spots">
                  <Navigation className="w-6 h-6 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground/60 mb-1">No spots found nearby</p>
                  <p className="text-[10px] text-muted-foreground/30 mb-3">Allow location access or add a spot</p>
                  <button onClick={() => setShowAddModal(true)} className="inline-flex items-center gap-1.5 bg-primary/15 border border-primary/20 text-primary rounded-xl px-4 py-2 text-xs font-medium hover:bg-primary/25 transition-colors" data-testid="nearby-add-spot-btn">
                    <Plus className="w-3 h-3" /> Add a Spot
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {nearbySpots.map((spot, i) => {
                    const name = typeof spot === 'string' ? spot : spot.name;
                    return (
                      <motion.div key={name} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                        onClick={() => setSelectedSpot(name)}
                        className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4 flex items-center gap-3 cursor-pointer hover:border-white/10 transition-all" data-testid={`nearby-spot-${i}`}>
                        <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                          <Navigation className="w-3.5 h-3.5 text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{name}</p>
                          {spot.distance_km && <p className="text-[9px] text-muted-foreground/40">{spot.distance_km} km away</p>}
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/30" />
                      </motion.div>
                    );
                  })}
                </div>
              )
            ) : tab === 'faves' ? (
              starredSpots.length === 0 ? (
                <div className="text-center py-12"><Star className="w-6 h-6 text-yellow-400/30 mx-auto mb-2" /><p className="text-xs text-muted-foreground">no faves yet — star spots to add them</p></div>
              ) : (
                <div className="space-y-2">
                  {starredSpots.map(name => (
                    <motion.div key={name} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      onClick={() => setSelectedSpot(name)}
                      className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4 flex items-center gap-3 cursor-pointer hover:border-white/10 transition-all">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                      <p className="text-xs font-semibold truncate flex-1">{name}</p>
                      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/30" />
                    </motion.div>
                  ))}
                </div>
              )
            ) : tab === 'liked' ? (
              Object.entries(reactions).filter(([,r]) => r === 'shaka').length === 0 ? (
                <div className="text-center py-12"><ShakaIcon className="w-6 h-6 text-primary/30 mx-auto mb-2" /><p className="text-xs text-muted-foreground">no liked spots yet — shaka spots you dig</p></div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(reactions).filter(([,r]) => r === 'shaka').map(([name]) => (
                    <motion.div key={name} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      onClick={() => setSelectedSpot(name)}
                      className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4 flex items-center gap-3 cursor-pointer hover:border-white/10 transition-all">
                      <ShakaIcon className="w-4 h-4 text-primary flex-shrink-0" />
                      <p className="text-xs font-semibold truncate flex-1">{name}</p>
                      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/30" />
                    </motion.div>
                  ))}
                </div>
              )
            ) : loading ? (
              <div className="flex items-center justify-center py-16"><div className="flex space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div><div className="w-2 h-2 bg-primary rounded-full typing-dot"></div><div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
              </div></div>
            ) : (
              <div className="space-y-2">
                {spots.map((spot) => (
                  <SpotCard key={`${spot.name}-${spot.source}-${spot.lat}`} spot={spot} isStarred={starredSpots.includes(spot.name)} myReaction={reactions[spot.name.toLowerCase()] || null} onStar={toggleStar} onReact={handleReact} onClick={(s) => setSelectedSpot(s.name)} />
                ))}
                {spots.length === 0 && (
                  <div className="text-center py-12">
                    <MapPin className="w-6 h-6 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">no spots found</p>
                  </div>
                )}
                {/* Load more */}
                {hasMore && (
                  <button
                    data-testid="spots-load-more"
                    onClick={() => loadSpots(page + 1, true)}
                    disabled={loadingMore}
                    className="w-full py-3 mt-2 rounded-xl bg-white/5 border border-white/10 text-xs text-muted-foreground hover:bg-white/10 hover:text-foreground transition-all flex items-center justify-center gap-2"
                  >
                    {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    {loadingMore ? 'Loading...' : `Load more (${(totalCount - spots.length).toLocaleString()} remaining)`}
                  </button>
                )}
              </div>
            )}

            {/* Attribution */}
            <div className="flex items-center justify-center gap-1.5 mt-4 mb-2 text-[9px] text-muted-foreground/40" data-testid="spitcast-attribution">
              <Waves className="w-3 h-3" />
              <span>Spot data powered by <span className="text-primary/50 font-medium">Spitcast</span> & <span className="text-primary/50 font-medium">Surfline</span></span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Spot Modal */}
      <AnimatePresence>
        {showAddModal && <AddSpotModal onAdd={handleSpotAdded} onClose={() => setShowAddModal(false)} />}
      </AnimatePresence>
    </PageShell>
  );
}
